<script setup>
import { ref, watch } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Switch from 'next/switch/Switch.vue';

// 服务端浏览器黑名单拦截(webwidget_triggered 阶段)。
// 默认拦截 "Unknown Browser"(爬虫/无头客户端, widget 解析不出浏览器型号)。
const DEFAULT_PATTERNS = ['Unknown Browser'];

const { currentAccount, updateAccount } = useAccount();

const isEnabled = ref(true);
const patterns = ref([...DEFAULT_PATTERNS]);
const newPattern = ref('');

const syncFromAccount = () => {
  const s = currentAccount.value?.settings || {};
  isEnabled.value = s.browser_block_filter_enabled !== false;
  const list = s.browser_block_list;
  patterns.value = Array.isArray(list) && list.length ? [...list] : [...DEFAULT_PATTERNS];
};

watch(currentAccount, syncFromAccount, { deep: true, immediate: true });

const persistList = async list => {
  try {
    await updateAccount({ browser_block_list: list });
  } catch (error) {
    useAlert('保存失败');
  }
};

const toggleFilter = async () => {
  try {
    await updateAccount({ browser_block_filter_enabled: isEnabled.value });
    useAlert(`浏览器过滤已${isEnabled.value ? '开启' : '关闭'}`);
  } catch (error) {
    useAlert('更新失败');
  }
};

const addPattern = async () => {
  const v = newPattern.value.trim();
  if (!v) return;
  if (patterns.value.some(p => p.toLowerCase() === v.toLowerCase())) {
    newPattern.value = '';
    return;
  }
  patterns.value = [...patterns.value, v];
  newPattern.value = '';
  await persistList(patterns.value);
  useAlert(`已添加拦截词:${v}`);
};

const removePattern = async idx => {
  const removed = patterns.value[idx];
  patterns.value = patterns.value.filter((_, i) => i !== idx);
  await persistList(patterns.value);
  useAlert(`已移除拦截词:${removed}`);
};

const onInputKeyup = e => {
  if (e.key === 'Enter') addPattern();
};
</script>

<template>
  <div class="p-4 rounded-lg border border-n-slate-4 bg-n-surface-1">
    <!-- 顶部: 标题 + 开关 -->
    <div class="flex items-start justify-between gap-4">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">浏览器过滤(服务端)</h3>
        <p class="text-xs text-n-slate-11 mt-1">
          打开网页自动建会话时, 按浏览器型号拦截爬虫/无头客户端。命中拦截词的不建会话。
        </p>
      </div>
      <Switch v-model="isEnabled" @change="toggleFilter" />
    </div>

    <!-- 拦截词编辑 -->
    <div v-if="isEnabled" class="mt-4">
      <div class="text-xs font-medium text-n-slate-11 mb-2">
        拦截词(命中 browser_name 任一子串即拦, 不区分大小写)
      </div>
      <div class="flex flex-wrap gap-1.5 mb-2">
        <span
          v-for="(p, idx) in patterns"
          :key="p + idx"
          class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-n-slate-3 dark:bg-n-solid-2 text-n-slate-11 font-mono"
        >
          {{ p }}
          <button
            type="button"
            class="text-n-slate-9 hover:text-n-ruby-9"
            title="移除"
            @click="removePattern(idx)"
          >
            ×
          </button>
        </span>
        <span v-if="!patterns.length" class="text-xs text-n-slate-9">(空, 不拦截任何浏览器)</span>
      </div>
      <div class="flex items-center gap-2">
        <input
          v-model="newPattern"
          type="text"
          placeholder="新增拦截词, 如 Unknown / Bot ..."
          class="flex-1 h-8 px-2 text-xs rounded-md border border-n-weak bg-n-input-background text-n-slate-12 outline-none focus:border-n-brand"
          @keyup="onInputKeyup"
        />
        <button
          type="button"
          class="h-8 px-3 text-xs rounded-md bg-n-brand text-white hover:opacity-90"
          @click="addPattern"
        >
          添加
        </button>
      </div>

      <div class="mt-3 p-2.5 rounded text-xs text-n-slate-11 bg-n-amber-9/10 border border-n-amber-9/20">
        <b>说明:</b> 此过滤只挡「打开页面自动建会话」。
        真访客(含被误判的)只要<b>真发一条消息</b>, 仍正常建会话并分配客服、AI 照常回复, 不受影响。
        默认 <code>Unknown Browser</code> 是 widget 无法识别浏览器型号时的值(典型爬虫/无头客户端特征)。
      </div>
    </div>
  </div>
</template>
